<template>
    <!-- The budget IS the layout.

         This was a wall of 48 cards with a "2 remaining" counter above it and
         the art it was choosing between painted at about a tenth of its
         strength behind the words. Both halves of that are wrong: the pictures
         are the thing being chosen and they were invisible, and a number is a
         poor way to say "you get two" when two empty frames say it without
         being read.

         So the screen is the two frames, big, with the question between them —
         and the catalogue is the same endless ring the portrait step uses. You
         can see how many you get, which you have taken, and what each one
         looks like, without counting anything. -->
    <div class="pl-slots" :class="isPair ? 'pl-slots--pair' : 'pl-slots--row'">
        <div class="pl-slots__stage">
            <template v-for="(slot, index) in slots" :key="index">
                <button
                    type="button"
                    class="pl-slot"
                    :class="{ 'is-filled': !!slot }"
                    :aria-label="slot ? t('player.creation.slots.clear', {name: labelOf(slot)}) : t('player.creation.slots.empty')"
                    :disabled="!slot"
                    @click="slot && toggle(slot.id)"
                >
                    <template v-if="slot">
                        <span class="pl-slot__art" :style="artOf(slot)" aria-hidden="true"></span>
                        <span class="pl-slot__veil" aria-hidden="true"></span>
                        <span class="pl-slot__body">
                            <span class="pl-slot__name">{{ labelOf(slot) }}</span>
                            <span class="pl-slot__prose">{{ proseOf(slot) }}</span>
                        </span>
                        <span class="pl-slot__drop" aria-hidden="true">
                            <i class="fas fa-xmark"></i>
                        </span>
                    </template>

                    <span v-else class="pl-slot__empty">
                        <i class="fas fa-plus" aria-hidden="true"></i>
                        {{ t('player.creation.slots.empty') }}
                    </span>
                </button>

                <!-- Between the two frames, so the question stands where the
                     answers will. With any other count it goes above them. -->
                <div v-if="isPair && index === 0" class="pl-slots__ask">
                    <h1 ref="heading" tabindex="-1" class="pl-fork__question">{{ title }}</h1>
                    <p v-if="hint" class="pl-slots__hint">{{ hint }}</p>
                </div>
            </template>

            <div v-if="!isPair" class="pl-slots__ask">
                <h1 ref="heading" tabindex="-1" class="pl-fork__question">{{ title }}</h1>
                <p v-if="hint" class="pl-slots__hint">{{ hint }}</p>
            </div>
        </div>

        <div v-if="!items.length" class="pl-empty">
            <p class="pl-empty__hint">{{ emptyHint }}</p>
        </div>

        <LobbyStrip
            v-else
            class="pl-strip--catalogue"
            :class="{ 'is-full': isFull }"
            :items="items"
            :selected-id="null"
            :label="labelOf"
            :aria-label="title"
            @select="toggle"
        >
            <template #default="{ item }">
                <span class="pl-mini__art" :style="artOf(item)" aria-hidden="true"></span>
                <span
                    v-if="isSelected(item.id)"
                    class="pl-mini__taken"
                    :aria-label="t('player.creation.slots.taken')"
                >
                    <i class="fas fa-check" aria-hidden="true"></i>
                </span>
                <span class="pl-mini__name">{{ labelOf(item) }}</span>
            </template>
        </LobbyStrip>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import LobbyStrip from '@/components/Player/LobbyStrip.vue';
import {backgroundImageOf} from '@/components/Player/mediaUrl.js';
import {cardTextOf} from '@/components/Player/Creation/cardText.js';

const props = defineProps({
    title: {type: String, required: true},
    hint: {type: String, default: ''},
    emptyHint: {type: String, default: ''},
    items: {type: Array, default: () => []},
    modelValue: {type: Array, default: () => []},
    max: {type: Number, default: 0},
    /* Namespaces the translation lookup. Two rows in different tables can share
       a name, so the slug alone is not unique across the flow. */
    kind: {type: String, default: ''},
});

const emit = defineEmits(['update:modelValue']);

const {t, te} = useI18n();
const heading = ref(null);

function labelOf(item) {
    return cardTextOf(t, te, props.kind, item).name;
}

function proseOf(item) {
    return cardTextOf(t, te, props.kind, item).description;
}

function artOf(item) {
    const image = backgroundImageOf(item?.icon);
    return image ? {backgroundImage: image} : null;
}

/* One entry per allowance, in the order they were taken, padded with nulls.
   The empty frames are the point: they are what says how many you get. */
const slots = computed(() => {
    const taken = props.modelValue
        .map((id) => props.items.find((item) => item.id === id))
        .filter(Boolean);
    return Array.from({length: Math.max(props.max, taken.length)}, (_, i) => taken[i] ?? null);
});

const isPair = computed(() => slots.value.length === 2);

/* Every frame is standing full. Nothing new can be taken until one is given
   back, and the catalogue says so by going grey rather than by refusing a
   click nobody was told about. */
const isFull = computed(() => props.max > 0 && props.modelValue.length >= props.max);

function isSelected(id) {
    return props.modelValue.includes(id);
}

/* Deselect always works, even at the cap — otherwise a player who fills the
   budget can never change their mind, which is the classic way a bounded
   multi-select traps someone. A full stage refuses a new pick, and the frames
   themselves are what make that refusal legible: there is visibly nowhere for
   it to go, and clicking a filled frame empties it. */
function toggle(id) {
    if (isSelected(id)) {
        emit('update:modelValue', props.modelValue.filter((value) => value !== id));
        return;
    }
    if (props.max > 0 && props.modelValue.length >= props.max) return;
    emit('update:modelValue', [...props.modelValue, id]);
}

onMounted(async () => {
    await nextTick();
    heading.value?.focus();
});
</script>
